import { useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/shared/ui/Card';
import { Button } from '@/shared/ui/Button';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/Toast';
import patterns from '@/shared/ui/styles/patterns.module.css';
import {
  campaignReviewApi,
  type CampaignReviewDecision,
  type CampaignReviewItem,
} from '../../api/campaignReviewApi';
import { campaignReviewKeys } from '../../queryKeys';
import styles from './CampaignsToCheckCard.module.css';

interface Props {
  practiceId: string;
  locationId: string;
}

type Mode = 'unsure' | 'pausedNoSpend' | 'skipped' | 'markedOurs' | 'waiting';

const PLATFORM_LABEL = { google: 'Google', meta: 'Meta' } as const;

/** "Mt. Diablo Family Dentists" -> "Mt. Diablo Family Dentists'"; "Bright Smile" -> "Bright Smile's". */
const possessive = (name: string) => (name.endsWith('s') ? `${name}'` : `${name}'s`);

/** How long to wait before re-reading while a decision is waiting for its sync. */
const WAITING_REFETCH_MS = 4000;

/**
 * Campaigns on a shared ad account that the matcher could not place (or placed as
 * someone else's). Staff say "Ours" or "Not ours"; the choice applies on the next
 * sync. Shown whenever the account is shared or this location already has matches.
 */
export function CampaignsToCheckCard({ practiceId, locationId }: Props) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const queryKey = campaignReviewKeys.list(practiceId, locationId);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => campaignReviewApi.get(practiceId, locationId),
    refetchInterval: (query) =>
      (query.state.data?.waiting.length ?? 0) > 0 ? WAITING_REFETCH_MS : false,
  });

  const decideMutation = useMutation({
    mutationFn: (v: { matchId: string; decision: CampaignReviewDecision }) =>
      campaignReviewApi.decide(practiceId, locationId, v.matchId, v.decision),
    onSuccess: (result) => {
      if (!result.queued && !result.unchanged) {
        toast.error('Saved, but the sync could not be queued. It applies on the next scheduled sync.');
      }
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: Error) => toast.error(err.message ?? 'Could not save the decision.'),
  });

  // Blocks a second click that lands before React has re-rendered the disabled buttons.
  const inFlight = useRef(false);
  const decide = useCallback(
    (matchId: string, decision: CampaignReviewDecision) => {
      if (inFlight.current) return;
      inFlight.current = true;
      decideMutation.mutate(
        { matchId, decision },
        {
          onSettled: () => {
            inFlight.current = false;
          },
        },
      );
    },
    [decideMutation],
  );

  if (isLoading) {
    return (
      <Card title="Campaigns to check">
        <Spinner size="sm" />
      </Card>
    );
  }
  if (isError) {
    return (
      <Card title="Campaigns to check">
        <p className={styles.empty}>
          Could not load the campaigns to check.
        </p>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </Card>
    );
  }
  if (!data?.visible) return null;

  const pausedNoSpend = data.pausedNoSpend ?? [];
  const pendingVars = decideMutation.isPending ? decideMutation.variables : undefined;

  const renderItem = (item: CampaignReviewItem, mode: Mode) => (
    <li key={item.id} className={styles.item} data-testid="campaign-check-item">
      <div className={styles.itemMain}>
        <div className={styles.itemHead}>
          <span>{item.campaignName}</span>
          <span className={patterns.pillMuted}>{PLATFORM_LABEL[item.platform]}</span>
          {mode === 'waiting' && (
            <span className={patterns.pillInfo}>
              {item.verdict === 'keep' ? 'Marked ours' : 'Marked not ours'}
            </span>
          )}
        </div>
        {item.launchedElsewhere !== null && mode !== 'waiting' && (
          <span className={styles.refusal} data-testid="launched-elsewhere-note">
            Launched from {possessive(item.launchedElsewhere)} dashboard — it belongs there.
          </span>
        )}
        {mode === 'waiting' ? (
          <span className={styles.waitingNote}>
            {item.verdict === 'keep'
              ? 'Will appear after the next sync'
              : 'Will be removed after the next sync'}
          </span>
        ) : item.reasons.length > 0 ? (
          <ul className={styles.reasons}>
            {item.reasons.map((r) => (
              <li key={`${r.signal}-${r.detail}`}>{r.detail}</li>
            ))}
          </ul>
        ) : (
          <span className={styles.noReasons}>No signal tied this campaign to any practice.</span>
        )}
      </div>
      {mode !== 'waiting' && (
        <div className={styles.actions}>
          {item.launchedElsewhere === null && (
            <Button
              variant="primary"
              size="sm"
              loading={pendingVars?.matchId === item.id && pendingVars.decision === 'ours'}
              disabled={decideMutation.isPending}
              onClick={() => decide(item.id, 'ours')}
            >
              Ours
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            loading={pendingVars?.matchId === item.id && pendingVars.decision === 'not_ours'}
            disabled={decideMutation.isPending}
            onClick={() => decide(item.id, 'not_ours')}
          >
            Not ours
          </Button>
        </div>
      )}
    </li>
  );

  return (
    <Card title="Campaigns to check">
      <p className={styles.intro}>
        This ad account is shared with another practice. Only campaigns that belong to this practice
        are imported. Tell us which of these are ours; the choice applies on the next sync.
        {data.waiting.some((i) => i.verdict === 'keep') &&
          ' Older days (up to 90) of a campaign marked ours are filled in on the next sync.'}
      </p>

      {data.unsure.length === 0 && data.waiting.length === 0 && (
        <p className={styles.empty}>
          {pausedNoSpend.length === 0
            ? 'Nothing to check. Every campaign on this account has an owner.'
            : 'Nothing needs a decision right now.'}
        </p>
      )}

      {data.unsure.length > 0 && (
        <ul className={styles.list}>{data.unsure.map((i) => renderItem(i, 'unsure'))}</ul>
      )}

      {pausedNoSpend.length > 0 && (
        <details className={styles.skipped} data-testid="paused-no-spend">
          <summary>Paused, no recent spend ({pausedNoSpend.length})</summary>
          <ul className={styles.list}>{pausedNoSpend.map((i) => renderItem(i, 'pausedNoSpend'))}</ul>
        </details>
      )}

      {data.waiting.length > 0 && (
        <>
          <h4 className={styles.sectionTitle}>Waiting for sync ({data.waiting.length})</h4>
          <ul className={styles.list}>{data.waiting.map((i) => renderItem(i, 'waiting'))}</ul>
        </>
      )}

      {data.markedOurs.length > 0 && (
        <details className={styles.skipped}>
          <summary>Marked ours ({data.markedOurs.length})</summary>
          <ul className={styles.list}>{data.markedOurs.map((i) => renderItem(i, 'markedOurs'))}</ul>
        </details>
      )}

      {data.skipped.length > 0 && (
        <details className={styles.skipped}>
          <summary>Skipped ({data.skipped.length})</summary>
          <ul className={styles.list}>{data.skipped.map((i) => renderItem(i, 'skipped'))}</ul>
        </details>
      )}
    </Card>
  );
}
